import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

// Powiadomienia dla całej aplikacji.
//
// Wcześniej ContainerManagement i StockManagement miały każdy własny stan
// `message`, własną funkcję `showMessage` i własny setTimeout(3000) — a komunikat
// renderował się na górze widoku, więc przy przewiniętej liście operator nie
// widział, czy zapis się powiódł. Teraz komunikaty są jednym, wspólnym stosem
// wyświetlanym w rogu ekranu, niezależnie od pozycji przewijania.

const ToastContext = createContext(null);

const DEFAULT_DURATION = 4000;
// Błędy wiszą dłużej — komunikat o nieudanym zapisie musi zdążyć zostać przeczytany.
const ERROR_DURATION = 7000;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());
  const nextId = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (message, type = 'info', options = {}) => {
      const id = nextId.current;
      nextId.current += 1;

      const duration =
        options.duration ?? (type === 'error' ? ERROR_DURATION : DEFAULT_DURATION);

      setToasts((current) => [...current, { id, message, type, title: options.title }]);

      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration)
        );
      }

      return id;
    },
    [dismiss]
  );

  const value = useMemo(
    () => ({
      toasts,
      dismiss,
      show,
      success: (message, options) => show(message, 'success', options),
      error: (message, options) => show(message, 'error', options),
      info: (message, options) => show(message, 'info', options),
      warn: (message, options) => show(message, 'warn', options),
    }),
    [toasts, dismiss, show]
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast musi być użyty wewnątrz <ToastProvider>');
  return context;
}
